import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface Purchase {
  id: string;
  product_slug: string;
  product_name: string;
  purchased_at: string;
}

interface Profile {
  full_name: string;
  email: string;
}

export default function MiEspacioPage() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', user.id)
          .maybeSingle();

        if (profileData) {
          setProfile(profileData);
        } else {
          setProfile({
            full_name: user.user_metadata?.full_name || 'Usuario',
            email: user.email || ''
          });
        }

        const { data: purchasesData } = await supabase
          .from('user_purchases')
          .select('*')
          .eq('user_id', user.id)
          .order('purchased_at', { ascending: false });

        if (purchasesData) {
          setPurchases(purchasesData);
        }

        setLoadingData(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F7CDCE]/20 via-white to-[#BEA6BE]/10 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-[#BEA6BE] text-4xl"></i>
          <p className="mt-4 text-[#2C3E50]">Cargando tu espacio...</p>
        </div>
      </div>
    );
  }

  const products = [
    {
      id: 'raiz',
      name: 'Ritual Raíz',
      description: 'Conecta con tu esencia y tus raíces',
      color: 'from-[#A7A86C] to-[#D6D57B]',
      bgColor: 'bg-[#D6D57B]/20',
      borderColor: 'border-[#A7A86C]/30',
      icon: 'ri-seedling-line',
      contentUrl: '/contenido/raiz',
      productUrl: '/producto/raiz'
    },
    {
      id: 'cuerpo',
      name: 'Ritual Cuerpo',
      description: 'Habita tu cuerpo plenamente',
      color: 'from-[#BEA6BE] to-[#F7CDCE]',
      bgColor: 'bg-[#F7CDCE]/20',
      borderColor: 'border-[#BEA6BE]/30',
      icon: 'ri-heart-pulse-line',
      contentUrl: '/contenido/cuerpo',
      productUrl: '/producto/cuerpo'
    },
    {
      id: 'cielo',
      name: 'Ritual Cielo',
      description: 'Expande tu visión y propósito',
      color: 'from-[#94A5B7] to-[#BEA6BE]',
      bgColor: 'bg-[#94A5B7]/15',
      borderColor: 'border-[#94A5B7]/30',
      icon: 'ri-sparkling-line',
      contentUrl: '/contenido/cielo',
      productUrl: '/producto/cielo'
    },
  ];

  const freeRituals = [
    {
      id: 'musa-interior',
      title: 'La Musa que tienes dentro',
      description: 'Un ritual de 15 minutos para conectar con tu fuente de inspiración interna y despertar tu creatividad más auténtica',
      icon: 'ri-magic-line',
      duration: '15 min',
      color: 'from-[#F7CDCE] to-[#BEA6BE]',
      bgColor: 'bg-gradient-to-r from-[#F7CDCE]/15 to-[#BEA6BE]/10',
      borderColor: 'border-[#F7CDCE]/40',
      url: '/rituales/musa-interior'
    },
    {
      id: 'planeta-corazon',
      title: '¿De qué planeta es tu corazón?',
      description: 'Un viaje interior de 20 minutos para descubrir qué te mueve realmente y reconectar con tus deseos más profundos',
      icon: 'ri-planet-line',
      duration: '20 min',
      color: 'from-[#94A5B7] to-[#BEA6BE]',
      bgColor: 'bg-gradient-to-r from-[#94A5B7]/15 to-[#BEA6BE]/10',
      borderColor: 'border-[#94A5B7]/30',
      url: '/rituales/planeta-corazon'
    },
    {
      id: 'fuente-ideas',
      title: 'La fuente de las ideas',
      description: 'Un ritual de 18 minutos para conectar con tus voces inspiradoras y reconocer tu propia voz creativa',
      icon: 'ri-lightbulb-flash-line',
      duration: '18 min',
      color: 'from-[#A7A86C] to-[#D6D57B]',
      bgColor: 'bg-gradient-to-r from-[#D6D57B]/15 to-[#A7A86C]/10',
      borderColor: 'border-[#A7A86C]/30',
      url: '/rituales/fuente-ideas'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7CDCE]/10 via-white to-[#BEA6BE]/10">
      {/* Header */}
      <header className="bg-[#f3f9fe] backdrop-blur-sm shadow-sm border-b border-[#BEA6BE]/20">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/939789daf8d153a89283b287753dc8bd.png"
                alt="Una voz más que propia"
                className="h-16 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#2C3E50] hidden sm:block">
                Hola, <strong className="text-[#BEA6BE]">{profile?.full_name}</strong>
              </span>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-sm text-[#2C3E50] hover:text-[#BEA6BE] transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line"></i>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-serif text-[#BEA6BE] mb-3">
            Tu espacio personal
          </h1>
          <p className="text-[#2C3E50]">
            Aquí encontrarás todos tus rituales y contenidos adquiridos
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 border border-[#F7CDCE]/30 shadow-sm">
            <div className="w-12 h-12 bg-gradient-to-br from-[#F7CDCE]/30 to-[#BEA6BE]/20 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shopping-bag-line text-[#BEA6BE] text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-[#BEA6BE] mb-1">{purchases.length}</div>
            <div className="text-sm text-[#2C3E50]">Productos adquiridos</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#A7A86C]/20 shadow-sm">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D6D57B]/30 to-[#A7A86C]/20 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-quill-pen-line text-[#A7A86C] text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-[#A7A86C] mb-1">3</div>
            <div className="text-sm text-[#2C3E50]">Rituales gratuitos</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#BEA6BE]/20 shadow-sm">
            <div className="w-12 h-12 bg-gradient-to-br from-[#BEA6BE]/20 to-[#94A5B7]/20 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-heart-3-line text-[#BEA6BE] text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-[#2C3E50] mb-1">∞</div>
            <div className="text-sm text-[#2C3E50]">Acceso de por vida</div>
          </div>
        </div>

        {/* Free Rituals Section */}
        <div className="mb-16">
          {/* Section Header */}
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-[#F7CDCE]/15 via-[#BEA6BE]/10 to-[#D6D57B]/15 rounded-2xl"></div>
            <div className="relative px-6 py-5 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center">
                    <i className="ri-gift-2-line text-[#BEA6BE] text-xl"></i>
                  </div>
                  <h2 className="text-2xl font-serif text-[#BEA6BE]">Rituales Gratuitos</h2>
                </div>
                <p className="text-[#2C3E50] text-sm ml-13">Incluyen guía PDF descargable • Disponibles para siempre</p>
              </div>
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <i className="ri-file-pdf-2-line text-[#BEA6BE]"></i>
                <span className="text-sm font-medium text-[#2C3E50]">Solo PDF</span>
              </div>
            </div>
          </div>

          {/* Ritual Cards - Horizontal Layout */}
          <div className="space-y-4">
            {freeRituals.map((ritual) => (
              <div
                key={ritual.id}
                className={`${ritual.bgColor} rounded-2xl border-2 ${ritual.borderColor} overflow-hidden hover:shadow-xl transition-all duration-300 group`}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Left - Icon Section */}
                  <div className={`md:w-48 p-6 flex items-center justify-center bg-gradient-to-br ${ritual.color}`}>
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <i className={`${ritual.icon} text-4xl text-white`}></i>
                    </div>
                  </div>
                  
                  {/* Right - Content Section */}
                  <div className="flex-1 p-6 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#BEA6BE]">
                          {ritual.title}
                        </h3>
                        <span className="px-2.5 py-1 bg-[#A7A86C]/15 rounded-full text-xs font-semibold text-[#A7A86C] uppercase tracking-wide">
                          Gratis
                        </span>
                      </div>
                      <p className="text-[#2C3E50] text-sm leading-relaxed mb-3 md:mb-0 max-w-xl">
                        {ritual.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#2C3E50]">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line"></i>
                          {ritual.duration}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-file-pdf-2-line"></i>
                          Guía PDF incluida
                        </span>
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <Link
                      to={ritual.url}
                      className={`flex-shrink-0 px-6 py-3.5 bg-gradient-to-r ${ritual.color} text-white rounded-xl font-medium hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl group-hover:scale-105`}
                    >
                      <span>Comenzar ritual</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Banner */}
          <div className="mt-8 bg-white rounded-2xl p-6 border border-[#BEA6BE]/20 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#F7CDCE]/30 to-[#BEA6BE]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-[#BEA6BE] text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-[#BEA6BE] mb-2">¿Qué son los rituales gratuitos?</h4>
                <p className="text-sm text-[#2C3E50] leading-relaxed">
                  Son prácticas guiadas en formato PDF que puedes descargar y realizar a tu ritmo. Cada ritual incluye preguntas de reflexión, ejercicios de visualización y espacio para escribir. Son el punto de partida perfecto para comenzar tu viaje de autoconocimiento.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-[#BEA6BE] mb-6">Mis Rituales Premium</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {products.map((product) => {
              // Buscar compra con ambos formatos de slug
              const hasPurchased = purchases.some(p => 
                p.product_slug === product.id || 
                p.product_slug === `producto-${product.id}`
              );
              
              return (
                <div
                  key={product.id}
                  className={`${product.bgColor} rounded-xl p-6 border ${product.borderColor} relative overflow-hidden`}
                >
                  {hasPurchased && (
                    <div className="absolute top-4 right-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#A7A86C] to-[#D6D57B] rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                    </div>
                  )}
                  
                  <div className={`w-16 h-16 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center mb-4`}>
                    <i className={`${product.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#BEA6BE] mb-2">
                    {product.name}
                  </h3>
                  <p className="text-[#2C3E50] mb-4">
                    {product.description}
                  </p>
                  
                  {hasPurchased ? (
                    <button
                      onClick={() => {
                        window.scrollTo(0, 0);
                        navigate(product.contentUrl);
                      }}
                      className={`w-full px-6 py-3 bg-gradient-to-r ${product.color} text-white rounded-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
                    >
                      Acceder al Contenido
                    </button>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-[#2C3E50] mb-3">No adquirido</p>
                      <button
                        onClick={() => {
                          window.scrollTo(0, 0);
                          navigate(product.productUrl);
                        }}
                        className="w-full px-6 py-3 bg-white text-[#BEA6BE] rounded-lg font-medium hover:bg-[#F7CDCE]/20 transition-colors border border-[#BEA6BE]/30 whitespace-nowrap cursor-pointer"
                      >
                        Ver Producto
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Available Products - Only show if not all purchased */}
        {purchases.length < products.length && (
          <div>
            <h2 className="text-2xl font-serif text-[#BEA6BE] mb-6">
              {purchases.length > 0 ? 'Explora más rituales' : 'Rituales disponibles'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.filter(product => !purchases.some(p => 
                p.product_slug === product.id || 
                p.product_slug === `producto-${product.id}`
              )).map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl overflow-hidden border border-[#BEA6BE]/20 shadow-sm hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="relative h-40 overflow-hidden bg-gradient-to-br from-[#F7CDCE]/20 to-[#BEA6BE]/10">
                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${product.color}`}>
                      <i className={`${product.icon} text-6xl text-white opacity-80`}></i>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${product.bgColor}`}>
                        <i className={`${product.icon} text-lg`}></i>
                      </div>
                      <h3 className="text-lg font-semibold text-[#BEA6BE]">{product.name}</h3>
                    </div>
                    <p className="text-sm text-[#2C3E50] mb-4">{product.description}</p>
                    <Link
                      to={product.productUrl}
                      onClick={() => window.scrollTo(0, 0)}
                      className="w-full py-2.5 border-2 border-[#BEA6BE] text-[#BEA6BE] rounded-lg text-sm font-medium hover:bg-gradient-to-r hover:from-[#BEA6BE] hover:to-[#F7CDCE] hover:text-white hover:border-transparent transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      Ver detalles
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All purchased message */}
        {purchases.length === products.length && (
          <div className="text-center py-12 bg-white rounded-xl border border-[#BEA6BE]/20">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D6D57B]/20 to-[#A7A86C]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-star-smile-line text-[#A7A86C] text-3xl"></i>
            </div>
            <h3 className="text-xl font-serif text-[#BEA6BE] mb-2">¡Tienes todos los rituales premium!</h3>
            <p className="text-[#2C3E50] text-sm">Gracias por confiar en este viaje hacia tu voz</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-[#F7CDCE]/20 via-[#BEA6BE]/10 to-[#D6D57B]/15 border-t border-[#BEA6BE]/20 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm text-[#2C3E50]">
            © 2025 Una voz más que propia. Todos los derechos reservados.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-xs text-[#2C3E50]/70 hover:text-[#BEA6BE] transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
